'use client'

import { css, cx } from '@lastbrand/web-ui-styled-system/css'
import { useEffect } from 'react'

import { LoaderWrapperDS } from '@src/components/blocks/loader-wrapper-ds'
import { SearchResultsContentDS } from '@src/components/blocks/search-results-content-ds'
import { IProductCard } from '@src/components/product-card/product-card.types'
import BATCHED_EVENT_LIMIT from '@src/constants/batchedEventLimit'
import { SelectedFilter } from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import {
  getTrackingMetaData,
  PRODUCT_VIEW_TRACKER,
} from '@src/pages/search/searchResultsSection/searchResults/searchResults.constants'
import { batchedAnalytics } from '@src/quince-site-common/src/utils/batched-analytics'
import { getProductDataForBatchedAnalytics } from '@src/quince-site-common/src/utils/getProductDataForBatchedAnalytics'
import { getProductTagsDataForBatchedAnalytics } from '@src/quince-site-common/src/utils/getProductTagsDataForBatchedAnalytics'
import { runWhenMainThreadIdle } from '@src/quince-site-common/src/utils/interaction-scheduling'
import { isBrowser } from '@src/quince-site-common/src/utils/windowUtil'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

/* eslint-disable i18next/no-literal-string -- Panda design tokens, not user-facing copy */

const containerClass = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: 'full',
  minHeight: '26rem',
})

// Legacy `__paddingTop` — only applies on desktop when `?hide_header=true`.
const containerPaddingTopClass = css({
  pt: { base: '0', lg: 'space-40' },
})

// Bottom-loader band — replaces the legacy inline `<LoaderWrapper minHeight="20rem">`.
const moreLoaderClass = css({
  width: 'full',
})

/* eslint-enable i18next/no-literal-string */

export interface SearchResultsDSProps {
  searchQuery: string
  productCards: IProductCard[]
  intersectRef: (el: HTMLElement | null) => void
  isMoreProductsLoading: boolean
  onResetFilters: () => void
  selectedFilters: SelectedFilter
}

export const SearchResultsDS = ({
  searchQuery,
  productCards,
  intersectRef,
  isMoreProductsLoading,
  onResetFilters,
  selectedFilters,
}: SearchResultsDSProps) => {
  const url = isBrowser() && new URL(window.location.href)
  const hideSearchHeader = url && url.searchParams.get('hide_header') === 'true'

  useEffect(() => {
    batchedAnalytics.initializeBatchedAnalyticsEvent({
      tracker: PRODUCT_VIEW_TRACKER,
      eventLimit: BATCHED_EVENT_LIMIT[TELEMETRY_EVENT_NAMES.BULK_PRODUCT_CARD_VIEW],
      debouncePeriod: 1000,
    })
    batchedAnalytics.trackBatchedAnalytics({
      id: `internalAnalytics-${PRODUCT_VIEW_TRACKER}`,
      tracker: PRODUCT_VIEW_TRACKER,
      callback: ({ events }) => {
        runWhenMainThreadIdle(() => {
          const productList = events.map(event => getProductDataForBatchedAnalytics(event))
          const tagsCount = getProductTagsDataForBatchedAnalytics(productList)
          trackEvent(TELEMETRY_EVENT_NAMES.BULK_PRODUCT_CARD_VIEW, {
            productList: productList,
            tagsCount: tagsCount,
            ...getTrackingMetaData().context,
          })
        })
      },
    })
  }, [])

  return (
    <div className={cx(containerClass, hideSearchHeader && containerPaddingTopClass)}>
      {searchQuery && (
        <>
          <SearchResultsContentDS
            productCards={productCards}
            searchQuery={searchQuery}
            onResetFilters={onResetFilters}
            selectedFilters={selectedFilters}
          />
          {/* eslint-disable-next-line i18next/no-literal-string -- DOM id used by infinite-scroll observer */}
          <div id="boundary_bottom" ref={intersectRef} />
          <LoaderWrapperDS
            showLoader={isMoreProductsLoading}
            // eslint-disable-next-line i18next/no-literal-string -- CSS length value, not user-facing copy
            minHeight="20rem"
            loadWrapperClassName={moreLoaderClass}
          >
            <div />
          </LoaderWrapperDS>
        </>
      )}
    </div>
  )
}
